// @tennis-matches/services/ranking
// Central helpers for ranking derivation + sorting.
// Single source of truth for ordering: points desc, GD desc, GF desc, id asc.
//
// NOTE: This module is UI-agnostic and should be reused by selectors + views.
//
// statsRow shape is based on scoringservice.recomputeStats():
// { id, points, played, W, D, L, GF, GA, GD, avgGamesPerMatch }

function toNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function toInt(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
}

/** Prüft, ob ein Side-Array ein K.O.-Platzhalter ist (ko-rank, ko-winner, ko-loser, …). */
function isKoPlaceholder(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const first = arr[0];
  return (
    first === "ko-rank" ||
    first === "ko-winner" ||
    first === "ko-winner-vf" ||
    first === "ko-winner-r1" ||
    first === "ko-loser" ||
    first === "ko-loser-r1"
  );
}

/** Extrahiert Side-IDs aus Match-Side (A/B, teamA/teamB, side_1/side_2, …). */
function asTeamIds(x) {
  if (Array.isArray(x)) return x.map(String);
  if (x && typeof x === "object" && Array.isArray(x.players))
    return x.players.map(String);
  return [];
}

/**
 * Extrahiert feste Teams aus Schedule (Vorrunden): Runden iterieren, A/B aus Matches, KO-Platzhalter ausfiltern, nach min-max-Key deduplizieren.
 * @param {Array<Array>} schedule - Turnier-Schedule
 * @param {number} vorrundeCount - Anzahl Vorrunden (ohne K.O.)
 * @returns {Array<[number, number]>} Teams als sortierte Paare [min, max]
 */
function extractTeamsFromSchedule(schedule, vorrundeCount) {
  const rounds = Array.isArray(schedule)
    ? schedule.slice(0, Math.max(0, vorrundeCount))
    : [];
  const seen = new Set();
  const teams = [];
  for (const round of rounds) {
    const matches = Array.isArray(round) ? round : [];
    for (const m of matches) {
      const A = asTeamIds(m?.A ?? m?.a ?? m?.teamA ?? m?.side_1 ?? []);
      const B = asTeamIds(m?.B ?? m?.b ?? m?.teamB ?? m?.side_2 ?? []);
      if (
        A.length >= 2 &&
        B.length >= 2 &&
        !isKoPlaceholder(A) &&
        !isKoPlaceholder(B)
      ) {
        const keyA = [
          Math.min(Number(A[0]), Number(A[1])),
          Math.max(Number(A[0]), Number(A[1])),
        ].join("-");
        const keyB = [
          Math.min(Number(B[0]), Number(B[1])),
          Math.max(Number(B[0]), Number(B[1])),
        ].join("-");
        if (!seen.has(keyA)) {
          seen.add(keyA);
          teams.push([Number(A[0]), Number(A[1])].sort((a, b) => a - b));
        }
        if (!seen.has(keyB)) {
          seen.add(keyB);
          teams.push([Number(B[0]), Number(B[1])].sort((a, b) => a - b));
        }
      }
    }
  }
  return teams;
}

export function sortStatsRows(statsMap) {
  return Object.values(statsMap || {})
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const ap = toNum(a.points);
      const bp = toNum(b.points);
      if (bp !== ap) return bp - ap;

      const aGD = toNum(a.GD, toInt(a.GF) - toInt(a.GA));
      const bGD = toNum(b.GD, toInt(b.GF) - toInt(b.GA));
      if (bGD !== aGD) return bGD - aGD;

      const aGF = toNum(a.GF);
      const bGF = toNum(b.GF);
      if (bGF !== aGF) return bGF - aGF;

      return toInt(a.id) - toInt(b.id);
    });
}

export function getRankPosition(sortedStatsRows, playerId) {
  const pid = String(playerId ?? "").trim();
  if (!pid) return null;

  const rows = Array.isArray(sortedStatsRows) ? sortedStatsRows : [];
  const idx = rows.findIndex((r) => String(r?.id ?? "").trim() == pid);
  return idx >= 0 ? idx + 1 : null;
}

/**
 * Liefert den Anzeige-Rang gemäß Ranking-Tabelle (mit Tie-Logik).
 * Bei gleichen Punkten/GD/GF wird derselbe Rang vergeben (z.B. 4, 4, 6).
 * Sonderfall: Haben alle dieselben Stats (z.B. 0,0,0), wird die Tabellenposition
 * (1, 2, 3, …) genutzt, damit nicht alle „Rank 1“ anzeigen.
 * @param {Array} sortedStatsRows - nach Punkte, GD, GF sortierte Stats-Zeilen
 * @param {string} playerId - PID des Spielers
 * @returns {number|null} 1-basierter Anzeige-Rang oder null
 */
export function getDisplayRankPosition(sortedStatsRows, playerId) {
  const pid = String(playerId ?? "").trim();
  if (!pid) return null;

  const rows = Array.isArray(sortedStatsRows) ? sortedStatsRows : [];
  if (rows.length === 0) return null;

  // Sonderfall: Alle haben identische Stats (z.B. 0,0,0) → Tabellenposition nutzen
  const first = rows[0];
  if (!first) return null;
  const allSame = rows.every(
    (r) =>
      r && r.points === first.points && r.GD === first.GD && r.GF === first.GF,
  );
  if (allSame) {
    const idx = rows.findIndex((r) => String(r?.id ?? "").trim() === pid);
    return idx >= 0 ? idx + 1 : null;
  }

  let currentRank = 1;
  let prev = null;
  for (let idx = 0; idx < rows.length; idx++) {
    const r = rows[idx];
    if (!r) continue;
    if (
      !(
        prev &&
        prev.points === r.points &&
        prev.GD === r.GD &&
        prev.GF === r.GF
      )
    ) {
      currentRank = idx + 1;
    }
    prev = r;
    if (String(r?.id ?? "").trim() === pid) return currentRank;
  }
  return null;
}

export function buildRankingRows({ players, statsMap }) {
  const count = Number(players?.count ?? 0);
  const names = players?.names || {};

  const rows = [];
  for (let i = 1; i <= count; i++) {
    const id = String(i);
    const r = statsMap?.[id] || {};
    rows.push({
      id,
      name: String(names[id] || "").trim(),
      points: toNum(r.points),
      played: toInt(r.played),
      W: toInt(r.W),
      D: toInt(r.D),
      L: toInt(r.L),
      GF: toInt(r.GF),
      GA: toInt(r.GA),
      GD: toInt(r.GD, toInt(r.GF) - toInt(r.GA)),
      avg: r.avgGamesPerMatch == null ? null : toNum(r.avgGamesPerMatch),
    });
  }

  rows.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.GD !== a.GD) return b.GD - a.GD;
    if (b.GF !== a.GF) return b.GF - a.GF;
    return toInt(a.id) - toInt(b.id);
  });

  return rows;
}

export const RANKING_SORT_POINTS = "points";
export const RANKING_SORT_GAMES = "games";

/**
 * Sortiert Ranking-Zeilen für die Anzeige.
 * points: Punkte → GD → GF (bisherige Tabelle)
 * games: gewonnene Spiele (GF) → GD → Siege
 */
export function applyRankingSort(rows, mode = RANKING_SORT_POINTS) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const byGames = mode === RANKING_SORT_GAMES;
  list.sort((a, b) => {
    if (byGames) {
      if (toInt(b.GF) !== toInt(a.GF)) return toInt(b.GF) - toInt(a.GF);
      if (toInt(b.GD) !== toInt(a.GD)) return toInt(b.GD) - toInt(a.GD);
      if (toInt(b.W) !== toInt(a.W)) return toInt(b.W) - toInt(a.W);
      if (toNum(b.points) !== toNum(a.points))
        return toNum(b.points) - toNum(a.points);
    } else {
      if (toNum(b.points) !== toNum(a.points))
        return toNum(b.points) - toNum(a.points);
      if (toInt(b.GD) !== toInt(a.GD)) return toInt(b.GD) - toInt(a.GD);
      if (toInt(b.GF) !== toInt(a.GF)) return toInt(b.GF) - toInt(a.GF);
    }
    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""), "en");
  });
  let currentRank = 1;
  let prev = null;
  const tied = (x, y) => {
    if (!x || !y) return false;
    if (byGames)
      return toInt(x.GF) === toInt(y.GF) && toInt(x.GD) === toInt(y.GD);
    return (
      toNum(x.points) === toNum(y.points) &&
      toInt(x.GD) === toInt(y.GD) &&
      toInt(x.GF) === toInt(y.GF)
    );
  };
  return list.map((r, idx) => {
    if (!(prev && tied(prev, r))) currentRank = idx + 1;
    prev = r;
    return { ...r, __rank: currentRank };
  });
}

/**
 * Feste Doppel: Team-Ranking aus Vorrunde.
 * Liefert Map rank -> [playerIdA, playerIdB] für K.O.-Auflösung.
 * Teams werden nach Punkten sortiert (beide Partner haben gleiche Punkte).
 *
 * @param {Array<Array>} schedule - Turnier-Schedule (Runden)
 * @param {Object} statsMap - Player-Stats (von recomputeStats)
 * @param {number} vorrundeCount - Anzahl Vorrunden (K.O.-Runden ausschließen)
 * @returns {Record<number, [string, string]>} rankToTeam: { 1: ["1","5"], 2: ["2","7"], ... }
 */
export function getTeamRankingForFixedDoubles(
  schedule,
  statsMap,
  vorrundeCount,
) {
  const teams = extractTeamsFromSchedule(schedule, vorrundeCount);
  const map = statsMap || {};
  teams.sort((ta, tb) => {
    const ptsA =
      (toNum(map[String(ta[0])]?.points) + toNum(map[String(ta[1])]?.points)) /
      2;
    const ptsB =
      (toNum(map[String(tb[0])]?.points) + toNum(map[String(tb[1])]?.points)) /
      2;
    if (ptsB !== ptsA) return ptsB - ptsA;
    const gdA = toNum(map[String(ta[0])]?.GD, 0);
    const gdB = toNum(map[String(tb[0])]?.GD, 0);
    if (gdB !== gdA) return gdB - gdA;
    return ta[0] - tb[0];
  });

  const rankToTeam = {};
  teams.forEach((t, i) => {
    rankToTeam[i + 1] = [String(t[0]), String(t[1])];
  });
  return rankToTeam;
}

/**
 * Feste Doppel: Team-basierte Ranking-Zeilen.
 * Jede Zeile = ein Team (Name A & Name B), Stats aus einem Partner (beide identisch).
 *
 * @param {Array<Array>} schedule - Turnier-Schedule
 * @param {Object} statsMap - Player-Stats (recomputeStats)
 * @param {Object} players - { count, names }
 * @param {number} vorrundeCount - Anzahl Vorrunden (ohne K.O.)
 * @returns {Array<{ id, name, points, played, W, D, L, GF, GA, GD, __rank? }>}
 */

/**
 * Prüft, ob der Schedule feste Teams (2v2) enthält – für Rangliste als Team-Anzeige.
 * @param {Array<Array>} schedule - Turnier-Schedule
 * @param {number} vorrundeCount - Anzahl Vorrunden (ohne K.O.)
 * @returns {boolean}
 */
export function scheduleHasFixedTeams(schedule, vorrundeCount) {
  const teams = extractTeamsFromSchedule(schedule, vorrundeCount);
  return teams.length >= 2;
}

export function buildTeamRankingRows(
  schedule,
  statsMap,
  players,
  vorrundeCount,
) {
  const teams = extractTeamsFromSchedule(schedule, vorrundeCount);
  const names = players?.names || {};
  const map = statsMap || {};

  const rows = teams.map((t) => {
    const p1 = String(t[0]);
    const p2 = String(t[1]);
    const r = map[p1] || map[p2] || {};
    const n1 = String(names[p1] || "").trim();
    const n2 = String(names[p2] || "").trim();
    const teamName = n1 && n2 ? `${n1} & ${n2}` : n1 || n2 || `#${p1} & #${p2}`;
    return {
      id: `${p1}-${p2}`,
      name: teamName,
      points: toNum(r.points),
      played: toInt(r.played),
      W: toInt(r.W),
      D: toInt(r.D),
      L: toInt(r.L),
      GF: toInt(r.GF),
      GA: toInt(r.GA),
      GD: toInt(r.GD, toInt(r.GF) - toInt(r.GA)),
      avg: r.avgGamesPerMatch == null ? null : toNum(r.avgGamesPerMatch),
    };
  });

  rows.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.GD !== a.GD) return b.GD - a.GD;
    if (b.GF !== a.GF) return b.GF - a.GF;
    return String(a.id).localeCompare(String(b.id));
  });

  let currentRank = 1;
  let prev = null;
  return rows.map((r, idx) => {
    if (
      !(
        prev &&
        prev.points === r.points &&
        prev.GD === r.GD &&
        prev.GF === r.GF
      )
    )
      currentRank = idx + 1;
    prev = r;
    return { ...r, __rank: currentRank };
  });
}

/**
 * Feste Doppel: Liefert stabile Team-Nummern (1, 2, 3, …) pro Team.
 * Reihenfolge: kanonisch sortiert nach (min(p1,p2), max(p1,p2)) – unabhängig von Punkten.
 *
 * @param {Array<Array>} schedule - Schedule (nur Vorrunde relevant)
 * @param {number} vorrundeCount - Anzahl Vorrunden (ohne K.O.)
 * @returns {Record<string, number>} teamKey "p1-p2" -> teamNumber 1..n
 */
export function getTeamNumberMap(schedule, vorrundeCount) {
  const teams = extractTeamsFromSchedule(schedule, vorrundeCount);
  teams.sort((ta, tb) => {
    if (ta[0] !== tb[0]) return ta[0] - tb[0];
    return ta[1] - tb[1];
  });

  const map = {};
  teams.forEach((t, i) => {
    map[`${t[0]}-${t[1]}`] = i + 1;
  });
  return map;
}

/**
 * Hilfsfunktion: Team-Zeile aus [p1, p2] und players.names bauen.
 */
function teamRowFromPair(p1, p2, names) {
  const n1 = String(names?.[String(p1)] || "").trim();
  const n2 = String(names?.[String(p2)] || "").trim();
  const teamName = n1 && n2 ? `${n1} & ${n2}` : n1 || n2 || `#${p1} & #${p2}`;
  return { id: `${p1}-${p2}`, name: teamName, points: 0, __rank: 0 };
}

/**
 * Feste Doppel + Playoffs: Ermittelt Platz 1–3 aus K.O.-Ergebnissen.
 * Sieger = Team A oder B aus Finale; Verlierer = anderes Team; Platz 3 aus kleinem Finale.
 *
 * @param {Array<Array>} schedule - Schedule (inkl. K.O.-Runden)
 * @param {Object} results - Match-Ergebnisse { r1m1: { outcome, sets, ... }, ... }
 * @param {Record<number, [string, string]>} rankToTeamMap - rank -> [pid1, pid2]
 * @param {Object} players - { names }
 * @param {number} koTeilnehmer - 2, 4, 6 oder 8
 * @param {boolean} playThirdPlace - Platz 3 ausspielen
 * @returns {Array<{ id, name, points, __rank }>} top3 für Podium
 */
export function buildPlayoffFinishOrder(
  schedule,
  results,
  rankToTeamMap,
  players,
  koTeilnehmer,
  playThirdPlace,
) {
  const names = players?.names || {};
  const k = Number(koTeilnehmer) || 4;
  const rt = rankToTeamMap || {};
  const getResult = (matchKey) => {
    const r = results?.[matchKey];
    const oc = r?.outcome;
    if (oc === "A" || oc === "B") return oc;
    return null;
  };

  const rows = Array.isArray(schedule) ? schedule : [];
  const vorrundeCount = rows.length - (k === 2 ? 1 : k === 4 ? 2 : 3);
  const n = Math.max(0, vorrundeCount);

  const top3 = [];

  if (k === 2) {
    const finalMatch = rows[n]?.[0];
    const finalKey = finalMatch?.id || `r${n + 1}m1`;
    const outcome = getResult(finalKey);
    const team1 = rt[1];
    const team2 = rt[2];
    if (team1 && team2) {
      const t1 = teamRowFromPair(team1[0], team1[1], names);
      const t2 = teamRowFromPair(team2[0], team2[1], names);
      if (outcome === "A") {
        top3.push({ ...t1, __rank: 1 }, { ...t2, __rank: 2 });
      } else if (outcome === "B") {
        top3.push({ ...t2, __rank: 1 }, { ...t1, __rank: 2 });
      }
    }
  } else if (k === 4) {
    const hf1 = rows[n]?.[0];
    const hf2 = rows[n]?.[1];
    const finalRound = rows[n + 1] || [];
    const finalMatch = Array.isArray(finalRound) ? finalRound[0] : finalRound;
    const platz3Match =
      playThirdPlace && Array.isArray(finalRound) ? finalRound[1] : null;

    const hf1Key = hf1?.id || `r${n + 1}m1`;
    const hf2Key = hf2?.id || `r${n + 1}m2`;
    const oc1 = getResult(hf1Key);
    const oc2 = getResult(hf2Key);

    const team1 = rt[1];
    const team2 = rt[2];
    const team3 = rt[3];
    const team4 = rt[4];
    if (!team1 || !team2 || !team3 || !team4) return top3;

    const w1 = oc1 === "A" ? team1 : team4;
    const l1 = oc1 === "A" ? team4 : team1;
    const w2 = oc2 === "A" ? team2 : team3;
    const l2 = oc2 === "A" ? team3 : team2;

    const finalKey = finalMatch?.id || `r${n + 2}m1`;
    const finalOutcome = getResult(finalKey);

    if (finalOutcome === "A") {
      top3.push({ ...teamRowFromPair(w1[0], w1[1], names), __rank: 1 });
      top3.push({ ...teamRowFromPair(w2[0], w2[1], names), __rank: 2 });
    } else if (finalOutcome === "B") {
      top3.push({ ...teamRowFromPair(w2[0], w2[1], names), __rank: 1 });
      top3.push({ ...teamRowFromPair(w1[0], w1[1], names), __rank: 2 });
    }

    if (playThirdPlace && platz3Match) {
      const p3Key = platz3Match.id || `r${n + 2}m2`;
      const p3Outcome = getResult(p3Key);
      if (p3Outcome === "A") {
        top3.push({ ...teamRowFromPair(l1[0], l1[1], names), __rank: 3 });
      } else if (p3Outcome === "B") {
        top3.push({ ...teamRowFromPair(l2[0], l2[1], names), __rank: 3 });
      }
    }
  } else if (k === 6 || k === 8) {
    const koRounds = k === 8 ? 3 : 3;
    const lastRoundIdx = n + koRounds - 1;
    const lastRound = rows[lastRoundIdx] || [];
    const finalMatch = Array.isArray(lastRound) ? lastRound[0] : lastRound;
    const platz3Match =
      playThirdPlace && Array.isArray(lastRound) ? lastRound[1] : null;

    let winnerHF1, loserHF1, winnerHF2, loserHF2;
    if (k === 8) {
      const vfRound = rows[n] || [];
      const hfRound = rows[n + 1] || [];
      const resolveVf = (vfIdx) => {
        const m = vfRound[vfIdx];
        const key = m?.id || `r${n + 1}m${vfIdx + 1}`;
        const oc = getResult(key);
        const r1 = rt[vfIdx * 2 + 1];
        const r2 = rt[vfIdx * 2 + 2];
        if (!r1 || !r2) return null;
        return oc === "A" ? r1 : r2;
      };
      const wvf1 = resolveVf(0);
      const wvf2 = resolveVf(1);
      const wvf3 = resolveVf(2);
      const wvf4 = resolveVf(3);
      if (!wvf1 || !wvf2 || !wvf3 || !wvf4) return top3;

      const hf1 = hfRound[0];
      const hf2 = hfRound[1];
      const hf1Key = hf1?.id || `r${n + 2}m1`;
      const hf2Key = hf2?.id || `r${n + 2}m2`;
      const ocHf1 = getResult(hf1Key);
      const ocHf2 = getResult(hf2Key);
      winnerHF1 = ocHf1 === "A" ? wvf1 : wvf4;
      loserHF1 = ocHf1 === "A" ? wvf4 : wvf1;
      winnerHF2 = ocHf2 === "A" ? wvf2 : wvf3;
      loserHF2 = ocHf2 === "A" ? wvf3 : wvf2;
    } else {
      const r1Round = rows[n] || [];
      const hfRound = rows[n + 1] || [];
      const resolveR1 = (idx) => {
        const m = r1Round[idx];
        const key = m?.id || `r${n + 1}m${idx + 1}`;
        const oc = getResult(key);
        const pairs = idx === 0 ? [rt[3], rt[6]] : [rt[4], rt[5]];
        if (!pairs[0] || !pairs[1]) return null;
        return oc === "A" ? pairs[0] : pairs[1];
      };
      const wvf1 = resolveR1(0);
      const wvf2 = resolveR1(1);
      if (!wvf1 || !wvf2 || !rt[1] || !rt[2]) return top3;

      const hf1 = hfRound[0];
      const hf2 = hfRound[1];
      const hf1Key = hf1?.id || `r${n + 2}m1`;
      const hf2Key = hf2?.id || `r${n + 2}m2`;
      const ocHf1 = getResult(hf1Key);
      const ocHf2 = getResult(hf2Key);
      winnerHF1 = ocHf1 === "A" ? rt[1] : wvf2;
      loserHF1 = ocHf1 === "A" ? wvf2 : rt[1];
      winnerHF2 = ocHf2 === "A" ? rt[2] : wvf1;
      loserHF2 = ocHf2 === "A" ? wvf1 : rt[2];
    }

    const finalKey = finalMatch?.id || `r${lastRoundIdx + 1}m1`;
    const finalOutcome = getResult(finalKey);
    if (winnerHF1 && winnerHF2) {
      if (finalOutcome === "A") {
        top3.push({
          ...teamRowFromPair(winnerHF1[0], winnerHF1[1], names),
          __rank: 1,
        });
        top3.push({
          ...teamRowFromPair(winnerHF2[0], winnerHF2[1], names),
          __rank: 2,
        });
      } else if (finalOutcome === "B") {
        top3.push({
          ...teamRowFromPair(winnerHF2[0], winnerHF2[1], names),
          __rank: 1,
        });
        top3.push({
          ...teamRowFromPair(winnerHF1[0], winnerHF1[1], names),
          __rank: 2,
        });
      }
    }

    if (playThirdPlace && platz3Match && loserHF1 && loserHF2) {
      const p3Key = platz3Match.id || `r${lastRoundIdx + 1}m2`;
      const p3Outcome = getResult(p3Key);
      if (p3Outcome === "A") {
        top3.push({
          ...teamRowFromPair(loserHF1[0], loserHF1[1], names),
          __rank: 3,
        });
      } else if (p3Outcome === "B") {
        top3.push({
          ...teamRowFromPair(loserHF2[0], loserHF2[1], names),
          __rank: 3,
        });
      }
    }
  }

  return top3;
}
